import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { blogPosts } from "@/mocks/blog";
import { PHONE } from "@/mocks/updiag";
import type { BlogSection } from "@/mocks/blog";
import {
  schemaLocalBusiness,
  schemaBreadcrumb,
  schemaArticle,
  renderSchema,
} from "@/lib/schemas";

function renderSection(section: BlogSection, idx: number) {
  switch (section.type) {
    case "h2":
      return <h2 key={idx} className="text-2xl font-extrabold text-[#0F3B5F] mt-10 mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>{section.content}</h2>;
    case "h3":
      return <h3 key={idx} className="text-xl font-bold text-[#0F3B5F] mt-7 mb-3" style={{ fontFamily: "Barlow, sans-serif" }}>{section.content}</h3>;
    case "p":
      return <p key={idx} className="text-gray-600 leading-relaxed text-base mb-4">{section.content}</p>;
    case "ul":
      return (
        <ul key={idx} className="flex flex-col gap-2 mb-5 ml-2">
          {section.items?.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-gray-600 text-sm leading-relaxed">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                <i className="ri-checkbox-circle-fill text-[#F97316] text-base"></i>
              </div>
              {item}
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol key={idx} className="flex flex-col gap-3 mb-5">
          {section.items?.map((item, i) => (
            <li key={i} className="flex items-start gap-4 text-gray-600 text-sm leading-relaxed">
              <div className="w-7 h-7 flex items-center justify-center bg-[#0F3B5F] text-white rounded-full text-xs font-bold flex-shrink-0 mt-0.5">
                {i + 1}
              </div>
              {item}
            </li>
          ))}
        </ol>
      );
    case "callout": {
      const styles = {
        info: "bg-blue-50 border-blue-200 text-blue-800",
        warning: "bg-amber-50 border-amber-200 text-amber-800",
        success: "bg-green-50 border-green-200 text-green-800",
      };
      const icons = { info: "ri-information-line", warning: "ri-alert-line", success: "ri-checkbox-circle-line" };
      const variant = section.variant ?? "info";
      return (
        <div key={idx} className={`flex gap-3 p-5 rounded-xl border my-6 ${styles[variant]}`}>
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className={`${icons[variant]} text-lg`}></i>
          </div>
          <p className="text-sm leading-relaxed">{section.content}</p>
        </div>
      );
    }
    case "table":
      return (
        <div key={idx} className="overflow-x-auto my-6 rounded-xl border border-gray-100">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#0F3B5F] text-white">
                {section.headers?.map((h, i) => (
                  <th key={i} className="px-5 py-3 text-left font-semibold text-sm">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.rows?.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}>
                  {row.map((cell, ci) => (
                    <td key={ci} className={`px-5 py-3 text-gray-600 ${ci === row.length - 1 ? "font-semibold text-[#F97316]" : ""}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
}

export default function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [openFaq] = useState<number | null>(null);

  const post = blogPosts.find((p) => p.slug === slug);
  const related = blogPosts.filter((p) => post?.relatedSlugs.includes(p.slug)).slice(0, 3);

  useEffect(() => {
    if (!post) { navigate("/blog"); return; }
    document.title = `${post.title} | Up Diag`;
    window.scrollTo(0, 0);
  }, [post, navigate]);

  if (!post) return null;

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Inter, sans-serif" }}>
      <title>{post.title} | Blog Up Diag</title>
      <meta name="description" content={post.excerpt || post.title} />
      <link rel="canonical" href={`https://updiag.fr/blog/${post.slug}`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={renderSchema(schemaLocalBusiness())}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={renderSchema(
          schemaBreadcrumb([
            { name: "Accueil", url: "/" },
            { name: "Blog", url: "/blog" },
            { name: post.title, url: `/blog/${post.slug}` },
          ])
        )}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={renderSchema(
          schemaArticle(post.title, post.excerpt || post.title, post.image, post.category)
        )}
      />
      <Header />

      {/* Hero article */}
      <section className="relative h-72 md:h-96 overflow-hidden">
        <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F3B5F]/80 via-[#0F3B5F]/40 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8 max-w-4xl mx-auto">
          <span className="inline-block bg-[#F97316] text-white text-xs font-bold px-3 py-1.5 rounded-full mb-3">{post.category}</span>
          <h1 className="text-2xl md:text-3xl font-extrabold text-white leading-tight" style={{ fontFamily: "Barlow, sans-serif" }}>
            {post.title}
          </h1>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <nav className="flex items-center gap-2 text-sm text-gray-500 flex-wrap">
            <Link to="/" className="hover:text-[#F97316] transition-colors">Accueil</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/blog" className="hover:text-[#F97316] transition-colors">Blog</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#0F3B5F] font-medium line-clamp-1">{post.title}</span>
          </nav>
        </div>
      </div>

      {/* Contenu */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

          {/* Article */}
          <article className="lg:col-span-2">
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 mb-8 pb-6 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#0F3B5F] rounded-full flex items-center justify-center text-white text-xs font-bold">U</div>
                <div>
                  <div className="text-[#0F3B5F] font-semibold text-xs">{post.author}</div>
                  <div className="text-gray-400 text-xs">{post.authorRole}</div>
                </div>
              </div>
              <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{post.date}</span>
              <span className="flex items-center gap-1"><i className="ri-time-line"></i>{post.readTime} de lecture</span>
              <div className="flex flex-wrap gap-1.5 ml-auto">
                {post.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">{tag}</span>
                ))}
              </div>
            </div>

            {/* Intro */}
            <p className="text-lg text-gray-600 leading-relaxed mb-6 font-medium border-l-4 border-[#F97316] pl-5 italic">
              {post.excerpt}
            </p>

            {/* Sections */}
            <div>
              {post.content.map((section, idx) => renderSection(section, idx))}
            </div>

            {/* Tags */}
            <div className="mt-10 pt-6 border-t border-gray-100">
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1.5 bg-[#F8FAFC] text-[#334155] text-xs font-medium rounded-full border border-gray-100">{tag}</span>
                ))}
              </div>
            </div>

            {/* CTA inline */}
            <div className="mt-10 p-7 bg-[#0F3B5F] rounded-2xl text-center">
              <h3 className="text-xl font-extrabold text-white mb-2" style={{ fontFamily: "Barlow, sans-serif" }}>
                Besoin d'un diagnostic immobilier ?
              </h3>
              <p className="text-white/70 text-sm mb-5">Devis gratuit en 2 min — Intervention sous 48h — Rapport le jour même</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/devis" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap text-sm">
                  <i className="ri-file-list-3-line"></i>Devis gratuit
                </Link>
                <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-[#0F3B5F] rounded-xl font-bold hover:bg-gray-100 transition-all whitespace-nowrap text-sm">
                  <i className="ri-phone-fill"></i>{PHONE}
                </a>
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="flex flex-col gap-6">
            {/* Devis rapide */}
            <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100 sticky top-24">
              <h3 className="font-extrabold text-[#0F3B5F] text-base mb-1" style={{ fontFamily: "Barlow, sans-serif" }}>
                Devis gratuit en 2 min
              </h3>
              <p className="text-xs text-gray-400 mb-5">Réponse sous 2h — Sans engagement</p>
              <div className="flex flex-col gap-3">
                <Link to="/devis" className="flex items-center justify-center gap-2 w-full py-3.5 bg-[#F97316] text-white rounded-xl font-bold text-sm hover:bg-orange-600 transition-all whitespace-nowrap">
                  <i className="ri-file-list-3-line"></i>Obtenir mon devis
                </Link>
                <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="flex items-center justify-center gap-2 w-full py-3.5 bg-[#0F3B5F] text-white rounded-xl font-bold text-sm hover:bg-[#0a2d4a] transition-all whitespace-nowrap">
                  <i className="ri-phone-fill"></i>{PHONE}
                </a>
              </div>
              <div className="mt-5 pt-5 border-t border-gray-100 flex flex-col gap-2">
                {[
                  { icon: "ri-shield-check-line", text: "Certifié COFRAC" },
                  { icon: "ri-time-line", text: "Intervention sous 48h" },
                  { icon: "ri-file-check-line", text: "Rapport le jour même" },
                ].map((g) => (
                  <div key={g.text} className="flex items-center gap-2 text-xs text-gray-500">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${g.icon} text-[#F97316]`}></i>
                    </div>
                    {g.text}
                  </div>
                ))}
              </div>
            </div>

            {/* Articles liés */}
            {related.length > 0 && (
              <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100">
                <h3 className="font-extrabold text-[#0F3B5F] text-base mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>
                  Articles liés
                </h3>
                <div className="flex flex-col gap-4">
                  {related.map((r) => (
                    <Link key={r.slug} to={`/blog/${r.slug}`} className="group flex gap-3 cursor-pointer">
                      <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                        <img src={r.image} alt={r.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-[10px] font-bold text-[#F97316] uppercase tracking-wide">{r.category}</span>
                        <p className="text-xs font-semibold text-[#0F3B5F] leading-snug group-hover:text-[#F97316] transition-colors line-clamp-2 mt-0.5">{r.title}</p>
                        <span className="text-[10px] text-gray-400 mt-1 block">{r.readTime} de lecture</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Zones */}
            <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100">
              <h3 className="font-extrabold text-[#0F3B5F] text-base mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>
                Nos zones d'intervention
              </h3>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: "Paris 75", slug: "paris-75" },
                  { label: "Hauts-de-Seine 92", slug: "hauts-de-seine-92" },
                  { label: "Val-de-Marne 94", slug: "val-de-marne-94" },
                  { label: "Seine-Saint-Denis 93", slug: "seine-saint-denis-93" },
                  { label: "Essonne 91", slug: "essonne-91" },
                  { label: "Yvelines 78", slug: "yvelines-78" },
                ].map((z) => (
                  <Link key={z.slug} to={`/zones/${z.slug}`} className="px-3 py-1.5 bg-white border border-gray-100 rounded-full text-xs text-[#334155] hover:border-[#F97316] hover:text-[#F97316] transition-all cursor-pointer whitespace-nowrap">
                    {z.label}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Articles liés (bas de page) */}
      {related.length > 0 && (
        <section className="bg-[#F8FAFC] py-14">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-2xl font-extrabold text-[#0F3B5F] mb-8" style={{ fontFamily: "Barlow, sans-serif" }}>
              Articles similaires
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:-translate-y-1 hover:border-[#F97316] transition-all duration-300 cursor-pointer block">
                  <div className="h-40 overflow-hidden">
                    <img src={r.image} alt={r.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <span className="text-xs font-bold text-[#F97316] uppercase tracking-wide">{r.category}</span>
                    <h3 className="font-bold text-[#0F3B5F] text-sm leading-snug mt-1 mb-2 group-hover:text-[#F97316] transition-colors line-clamp-2" style={{ fontFamily: "Barlow, sans-serif" }}>{r.title}</h3>
                    <span className="text-xs text-gray-400">{r.date} · {r.readTime}</span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/blog" className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#0F3B5F] text-[#0F3B5F] rounded-xl font-bold hover:bg-[#0F3B5F] hover:text-white transition-all whitespace-nowrap text-sm">
                Voir tous les articles <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
